import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  BackButton,
  CancelButton,
  SubmitButton,
} from "../../../../../Components/Utils/Buttons";
import {
  NuDate,
  NuInput,
} from "../../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../../Components/Utils/Select";
import DropDownAddMetaData from "../../../../../Components/Utils/selectAddMetaData";
import MultiSelect from "../../../../../Components/Utils/MultiSelect";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import UseEditData from "../../../../../Components/Hooks/useEditData";
import UseDropDown from "../../../../../Components/Hooks/useDropDown";
import useFetchMataData from "../../../../../Components/Hooks/useFetchMataData";
import axiosInstance from "../../../../../Services/axiosCommon";
import NuDateDisplay from "../../../../../Components/Utils/NuDate";
import { FaPlus, FaTrash } from "react-icons/fa";

const STATUS_OPTIONS = [
  { value: "Scheduled", label: "Scheduled" },
  { value: "Ongoing", label: "Ongoing" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Draft", label: "Draft" },
];

const ACTION_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

export default function EditMeetingMinutesPage() {
  const { meetingPointId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const from = location.state?.from;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const { Oncancel, editData, contextHolder, isLoading } = UseEditData();
  const { data: usersList } = UseDropDown("/user/drop-down");
  const { value: categoryOptions, getData: refCategoryOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingMinutes_Category"
  );
  const { value: statusOptions, getData: refStatusOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingMinutes_Status"
  );
  const { value: actionStatusOptions } = useFetchMataData(
    "/dropdown/dropdown-details/",
    "MeetingMinutes_ActionStatus"
  );

  const [contractData, setContractData] = useState(stateData || null);
  const [participantsData, setParticipantsData] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [newAction, setNewAction] = useState({
    description: "",
    action_by: "",
    due_date: "",
    status: "Pending",
    comments: "",
  });

  // Fetch participants list for multiselect
  useEffect(() => {
    axiosInstance
      .get("/meetings/participants/?limit=500")
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        if (Array.isArray(list)) {
          setParticipantsData(list);
        }
      })
      .catch((err) => {
        console.warn("Could not fetch participants:", err);
      });
  }, []);

  const participantOptions = useMemo(() => {
    return (participantsData || []).map((p) => {
      const targetId = p.id || p.participant_id;
      const userName =
        p.name ||
        p.user_name ||
        p.userName ||
        p.user?.name ||
        `Participant #${targetId}`;
      const role = p.meeting_role || p.role || "Attendee";
      return {
        value: targetId,
        label: `${userName} (${role})`,
      };
    });
  }, [participantsData]);

  // Fetch meeting details
  useEffect(() => {
    if (!stateData && meetingPointId) {
      axiosInstance
        .get(`/meetings/points/${meetingPointId}`)
        .then((res) => {
          const m = res?.data?.data || res?.data;
          if (m) {
            setContractData(m);
            const items =
              m?.content?.action_items ||
              (Array.isArray(m?.content) ? m.content : []) ||
              m?.action_items ||
              [];
            setActionItems(items);
          }
        })
        .catch((err) => {
          console.error("Error fetching meeting details:", err);
        });
    } else if (stateData) {
      const items =
        stateData?.content?.action_items ||
        (Array.isArray(stateData?.content) ? stateData.content : []) ||
        stateData?.action_items ||
        [];
      setActionItems(items);
    }
  }, [stateData, meetingPointId]);

  const initialValues = useMemo(() => {
    let participantIds = [];
    if (Array.isArray(contractData?.participants)) {
      participantIds = contractData.participants.map((p) =>
        typeof p === "object" && p !== null ? p.id || p.participant_id : p
      );
    }

    return {
      meeting_title:
        contractData?.meeting_title || contractData?.title || "",
      date:
        (contractData?.date &&
          NuDateDisplay({
            value: contractData?.date,
            format: "YYYY-MM-DD",
          })) ||
        "",
      start_time: contractData?.start_time || "",
      end_time: contractData?.end_time || "",
      location: contractData?.location || "",
      category: contractData?.category || "",
      status: contractData?.status || "Scheduled",
      organizer_id:
        contractData?.organizer_id || contractData?.organizer?.id || "",
      participants: participantIds,
    };
  }, [contractData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: yup.object({
      meeting_title: yup.string().required("Meeting title is required!"),
      date: yup.string().required("Meeting date is required!"),
      start_time: yup.string().required("Start time is required!"),
      end_time: yup.string().required("End time is required!"),
      location: yup.string().required("Location is required!"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const targetId = meetingPointId || contractData?.id;
      const payload = {
        meeting_title: values.meeting_title,
        date: values.date,
        start_time:
          values.start_time?.length === 5
            ? `${values.start_time}:00`
            : values.start_time,
        end_time:
          values.end_time?.length === 5
            ? `${values.end_time}:00`
            : values.end_time,
        location: values.location,
        category: values.category || "",
        status: values.status || "Scheduled",
        organizer_id: values.organizer_id
          ? Number(values.organizer_id)
          : undefined,
        participants: Array.isArray(values.participants)
          ? values.participants.map(Number)
          : [],
        content: {
          action_items: actionItems.map((item) => ({
            description: item.description,
            action_by: item.action_by ? Number(item.action_by) : undefined,
            due_date: item.due_date || "",
            status: item.status || "Pending",
            comments: item.comments || "",
          })),
        },
      };

      editData(
        `/meetings/points/${targetId}`,
        payload,
        resetForm,
        from,
        { currentPage: pageIndex, searchQuery: searchQuery }
      );
    },
  });

  const handleAddActionItem = () => {
    if (!newAction.description.trim()) {
      alert("Please enter action item description");
      return;
    }
    setActionItems([...actionItems, { ...newAction, id: Date.now() }]);
    setNewAction({
      description: "",
      action_by: "",
      due_date: "",
      status: "Pending",
      comments: "",
    });
  };

  const handleRemoveActionItem = (indexToRemove) => {
    setActionItems(actionItems.filter((_, index) => index !== indexToRemove));
  };

  const handleActionItemChange = (index, field, val) => {
    const updated = [...actionItems];
    updated[index] = { ...updated[index], [field]: val };
    setActionItems(updated);
  };

  return (
    <div className="w-full h-full p-2 lg:px-4">
      <div className="flex justify-between items-center mb-4">
        <Breadcrumb
          items={[
            { name: "Meeting" },
            {
              name: "Meeting Minutes",
              link: "/meeting/meeting-points",
            },
            {
              name: "Edit Meeting Minutes",
            },
          ]}
        />
        <BackButton />
      </div>

      <div className="w-full h-[calc(100vh-140px)] overflow-y-auto">
        <form onSubmit={formik.handleSubmit}>
          <div className="w-full mx-auto px-2 pb-4 bg-white rounded-md">
            {/* Section 1: Meeting Details */}
            <div className="w-full">
              <p className="py-2 px-4 text-xs 2xl:text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                Edit Meeting Details :
              </p>
            </div>

            <div className="w-full flex justify-start content-start flex-wrap">
              <NuInput
                type="text"
                label="Meeting Title"
                placeholder="Enter Meeting Title"
                formik={formik}
                name="meeting_title"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuDate
                label="Meeting Date"
                placeholder="dd/mm/yyyy"
                formik={formik}
                name="date"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="time"
                label="Start Time"
                placeholder="HH:MM"
                formik={formik}
                name="start_time"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="time"
                label="End Time"
                placeholder="HH:MM"
                formik={formik}
                name="end_time"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <NuInput
                type="text"
                label="Location / Room"
                placeholder="Enter Location"
                formik={formik}
                name="location"
                isRequired={true}
                width="w-full md:w-1/2 lg:w-1/3"
              />

              <DropDownAddMetaData
                label="Category"
                placeholder="Select Category"
                Options={categoryOptions || []}
                formik={formik}
                name="category"
                width="w-full md:w-1/2 lg:w-1/3"
                getFunc={refCategoryOptions}
                displayProperty="Category"
                propertyName="MeetingMinutes_Category"
              />

              <DropDownAddMetaData
                label="Status"
                placeholder="Select Status"
                Options={statusOptions || STATUS_OPTIONS}
                formik={formik}
                name="status"
                width="w-full md:w-1/2 lg:w-1/3"
                getFunc={refStatusOptions}
                displayProperty="Status"
                propertyName="MeetingMinutes_Status"
              />

              <FormikSelectTwo
                label="Organizer"
                placeholder="Select Organizer"
                Options={usersList || []}
                formik={formik}
                name="organizer_id"
                width="w-full md:w-1/2 lg:w-1/3"
              />

              {/* Multiselect for Participants */}
              <MultiSelect
                label="Participants"
                placeholder="Select Participants"
                Options={participantOptions}
                formik={formik}
                name="participants"
                width="w-full md:w-1/2 lg:w-1/3"
              />
            </div>

            {/* Section 2: Action Items */}
            <div className="w-full mt-6">
              <p className="py-2 px-4 text-xs 2xl:text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                Action Items / Points :
              </p>
            </div>

            <div className="w-full px-4 py-2">
              {/* Add Action Item Inline Card */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
                <p className="text-xs font-semibold text-[#111537] mb-2">
                  Add New Action Point
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                  <div className="lg:col-span-2">
                    <label className="text-xs font-medium text-gray-700">
                      Description *
                    </label>
                    <input
                      type="text"
                      className="text-xs border border-gray-300 w-full h-9 px-3 mt-1 rounded-md outline-none focus:border-[#048178]"
                      placeholder="Action point description"
                      value={newAction.description}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-700">
                      Action By (Participant)
                    </label>
                    <select
                      className="text-xs border border-gray-300 w-full h-9 px-2 mt-1 rounded-md outline-none focus:border-[#048178] bg-white"
                      value={newAction.action_by}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          action_by: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Participant</option>
                      {participantOptions.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-700">
                      Due Date
                    </label>
                    <input
                      type="date"
                      className="text-xs border border-gray-300 w-full h-9 px-3 mt-1 rounded-md outline-none focus:border-[#048178] bg-white"
                      value={newAction.due_date}
                      onChange={(e) =>
                        setNewAction({
                          ...newAction,
                          due_date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleAddActionItem}
                      className="w-full h-9 bg-[#048178] hover:bg-[#036961] text-white text-xs font-semibold rounded-md flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <FaPlus className="text-xs" />
                      <span>Add Item</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Items List Table */}
              {actionItems.length > 0 && (
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-100 text-[#111537] font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-2.5 w-12 text-center">S.No</th>
                        <th className="p-2.5">Description</th>
                        <th className="p-2.5">Action By</th>
                        <th className="p-2.5">Due Date</th>
                        <th className="p-2.5">Status</th>
                        <th className="p-2.5">Comments</th>
                        <th className="p-2.5 w-16 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {actionItems.map((item, index) => {
                        return (
                          <tr key={index} className="hover:bg-slate-50/80">
                            <td className="p-2.5 text-center font-medium">
                              {index + 1}
                            </td>
                            <td className="p-2.5 font-medium">
                              {item.description}
                            </td>
                            <td className="p-2.5">
                              <select
                                value={item.action_by || ""}
                                onChange={(e) =>
                                  handleActionItemChange(
                                    index,
                                    "action_by",
                                    e.target.value
                                  )
                                }
                                className="text-xs border border-gray-300 rounded px-2 py-1 outline-none bg-white max-w-[170px]"
                              >
                                <option value="">Select Participant</option>
                                {participantOptions.map((p) => (
                                  <option
                                    key={p.value}
                                    value={p.value}
                                  >
                                    {p.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2.5">
                              <input
                                type="date"
                                className="text-xs border border-gray-200 rounded px-2 py-1 outline-none"
                                value={item.due_date || ""}
                                onChange={(e) =>
                                  handleActionItemChange(
                                    index,
                                    "due_date",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="p-2.5">
                              <select
                                value={item.status || "Pending"}
                                onChange={(e) =>
                                  handleActionItemChange(
                                    index,
                                    "status",
                                    e.target.value
                                  )
                                }
                                className="text-xs border border-gray-300 rounded px-2 py-1 outline-none bg-white"
                              >
                                {(actionStatusOptions || ACTION_STATUS_OPTIONS).map(
                                  (opt) => (
                                    <option
                                      key={opt.value || opt.id}
                                      value={opt.value || opt.label}
                                    >
                                      {opt.label || opt.value}
                                    </option>
                                  )
                                )}
                              </select>
                            </td>
                            <td className="p-2.5">
                              <input
                                type="text"
                                className="text-xs border border-gray-200 rounded px-2 py-1 w-full outline-none"
                                value={item.comments || ""}
                                placeholder="Add comments"
                                onChange={(e) =>
                                  handleActionItemChange(
                                    index,
                                    "comments",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="p-2.5 text-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveActionItem(index)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete Item"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Bottom Actions */}
            <div className="w-full flex justify-end gap-4 p-4 mt-6">
              <CancelButton
                handleClick={() => {
                  Oncancel(-1);
                }}
              />
              <SubmitButton isLoading={isLoading} />
            </div>
          </div>
        </form>
      </div>
      {contextHolder}
    </div>
  );
}
